import { MapPin, Phone, Mail, Clock, Car, CheckCircle2, Navigation, CalendarCheck, Package, Dumbbell } from "lucide-react";
import { Link } from "react-router-dom";
import { ExternalCta } from "@/components/common/ExternalCta";

// Full detailed location section (alternating layout) for the Locations page
export const LocationDetail = ({ item, index = 0 }) => {
  const reverse = index % 2 === 1;
  return (
    <div data-testid="location-detail" className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className={`grid lg:grid-cols-2 ${reverse ? "lg:[direction:rtl]" : ""}`}>
        {/* Image side */}
        <div className="relative min-h-[260px] lg:[direction:ltr]">
          {item.image_url && (
            <img src={item.image_url} alt={item.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
          <div className="relative flex h-full flex-col justify-end p-7">
            <div className="font-condensed text-xs font-semibold uppercase tracking-[0.25em] text-brand">Trilogy Facility</div>
            <h3 className="display-title text-4xl text-white sm:text-5xl">{item.name}</h3>
          </div>
        </div>

        {/* Info side */}
        <div className="p-7 sm:p-9 lg:[direction:ltr]">
          <div className="grid gap-2 text-sm">
            <p className="flex items-start gap-2.5 text-muted-foreground">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
              <span>{item.address}<br />{item.city}</span>
            </p>
            {item.phone && (
              <a href={`tel:${item.phone.replace(/[^0-9+]/g, "")}`} className="flex items-center gap-2.5 text-muted-foreground transition-colors hover:text-foreground">
                <Phone className="h-4 w-4 text-brand" />{item.phone}
              </a>
            )}
            {item.email && (
              <a href={`mailto:${item.email}`} className="flex items-center gap-2.5 text-muted-foreground transition-colors hover:text-foreground">
                <Mail className="h-4 w-4 text-brand" />{item.email}
              </a>
            )}
          </div>

          {/* Hours */}
          {Array.isArray(item.hours) && item.hours.length > 0 && (
            <div className="mt-6">
              <p className="flex items-center gap-2 font-condensed text-sm font-semibold uppercase tracking-wide"><Clock className="h-4 w-4 text-brand" /> Hours</p>
              <ul className="mt-2 grid gap-1 text-sm text-muted-foreground">
                {item.hours.map((h) => <li key={h}>{h}</li>)}
              </ul>
            </div>
          )}

          {/* Services */}
          {Array.isArray(item.services) && item.services.length > 0 && (
            <div className="mt-6">
              <p className="flex items-center gap-2 font-condensed text-sm font-semibold uppercase tracking-wide"><Dumbbell className="h-4 w-4 text-brand" /> Services Available</p>
              <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
                {item.services.map((s) => (
                  <li key={s} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-brand" />{s}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Amenities */}
          {Array.isArray(item.amenities) && item.amenities.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {item.amenities.map((a) => (
                <span key={a} className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">{a}</span>
              ))}
            </div>
          )}

          {/* Parking / instructions */}
          {item.parking && (
            <div className="mt-6 rounded-lg border border-border bg-background/50 p-4">
              <p className="flex items-center gap-2 font-condensed text-sm font-semibold uppercase tracking-wide"><Car className="h-4 w-4 text-brand" /> Parking &amp; Entry</p>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{item.parking}</p>
            </div>
          )}

          {/* Action buttons */}
          <div className="mt-7 flex flex-wrap gap-3">
            <ExternalCta href={item.map_url} variant="secondary" showIcon={false} testid="detail-directions-btn">
              <Navigation className="h-4 w-4 text-brand" /> Get Directions
            </ExternalCta>
            <ExternalCta href={item.booking_url} variant="primary" showIcon={false} testid="detail-book-btn">
              <CalendarCheck className="h-4 w-4" /> Book a Session
            </ExternalCta>
            <ExternalCta href={item.packages_url} variant="secondary" showIcon={false} testid="detail-packages-btn">
              <Package className="h-4 w-4 text-brand" /> View Packages
            </ExternalCta>
            <Link
              to="/contact"
              data-testid="detail-contact-btn"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-transparent px-5 py-3 text-sm font-semibold uppercase tracking-wide text-foreground transition-colors duration-200 hover:bg-accent active:scale-[0.98]"
            >
              <Mail className="h-4 w-4 text-brand" /> Contact
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
